<script>
  import { onMount, afterUpdate } from 'svelte'
  import { products } from '../../models/products.js'
  import { subscription } from '../../models/subscription.js'
  import { userRented } from '../../models/userRented.js'
  import { scrollToY } from '../../services/utilities.js'
  import RentalQuota from '../../components/RentalQuota.svelte'
  import RentalQuotaSubscription from '../../components/RentalQuotaSubscription.svelte'
  import ProductPreview from '../../components/ProductPreview.svelte'
  import ProductFilters, {
    defaultSort,
  } from '../../components/ProductFilters.svelte'
  import Popup from '../../components/Popup.svelte'

  const pageMeta = {
    1: {
      title: 'Rent Jigsaw Puzzles Online | Missing Piece Puzzle Exchange',
      heading: 'Rent Jigsaw Puzzles',
      description:
        'Browse hundreds of jigsaw puzzle rentals. Choose a puzzle subscription or one-time puzzle pack — all shipped free to your door.',
      canonical: 'https://www.puzzleexchange.com/rent',
    },
    2: {
      title: 'Buy Jigsaw Puzzles | Missing Piece Puzzle Exchange',
      heading: 'Buy Jigsaw Puzzles',
      description:
        'Shop discounted jigsaw puzzles from top brands like Ravensburger, Buffalo, Eurographics, and more.',
      canonical: 'https://www.puzzleexchange.com/shop',
    },
    3: {
      title: 'Puzzle Accessories | Missing Piece Puzzle Exchange',
      heading: 'Puzzle Accessories',
      description:
        'Shop jigsaw puzzle accessories including puzzle covers, puzzle glue sheets, and more.',
      canonical: 'https://www.puzzleexchange.com/accessories',
    },
    4: {
      title: 'Puzzle Packs | Missing Piece Puzzle Exchange',
      heading: 'Puzzle Packs',
      description:
        'One-time puzzle pack rentals — choose your puzzles and have them shipped to your door.',
      canonical: 'https://www.puzzleexchange.com/puzzle-packs',
    },
    5: {
      title: 'Puzzle Subscriptions | Missing Piece Puzzle Exchange',
      heading: 'Puzzle Subscriptions',
      description:
        'Monthly jigsaw puzzle subscription boxes. Choose your own puzzles, rent as many as your plan allows, and swap whenever you like.',
      canonical: 'https://www.puzzleexchange.com/puzzle-subscriptions',
    },
    6: {
      title: 'Gift Cards | Missing Piece Puzzle Exchange',
      heading: 'Gift Cards',
      description:
        'Give the gift of puzzles. Purchase a Missing Piece Puzzle Exchange gift card for any puzzle lover.',
      canonical: 'https://www.puzzleexchange.com/gift-cards',
    },
  }

  $: meta = pageMeta[params.id] || {}

  export let params
  let productTypeId = 0

  let filters = []
  let filtersOpen = false
  let search = ''
  let sortBy = ''
  let infoMsg = null
  let sale = false
  // The query string the product list was last loaded from; lets us detect
  // URL changes within the same product type (Apply Filters, back/forward)
  let currentSearch = null

  onMount(() => {
    let urlParams = new URLSearchParams(window.location.search)
    let referrer = urlParams.get('referrer')
    if (referrer === 'added-puzzlepack') {
      infoMsg =
        "You've added puzzle pack to your cart! Before checking out, choose up to 4 puzzles to rent. Enjoy!"
    } else if (referrer === 'added-mini-puzzlepack') {
      infoMsg =
        "You've added puzzle pack to your cart! Before checking out, choose up to 2 puzzles to rent. Enjoy!"
    }
  })

  function parseFilters() {
    let urlParams = new URLSearchParams(window.location.search)

    let opt = {
      query: [],
      search: '',
      sortBy: '',
    }
    let parsed = []

    // Note: URLSearchParams returns values already decoded, so re-encode
    // them when rebuilding the API query
    let categories = urlParams.getAll('category')
    for (var i = 0, len = categories.length; i < len; i++) {
      opt['query'].push(`category=${encodeURIComponent(categories[i])}`)
      parsed.push(`category-${categories[i]}`)
    }

    let attrNames = urlParams.getAll('attr-name')
    let attrVals = urlParams.getAll('attr-val')
    // Just a check to make sure there isn't an issue
    if (attrNames.length === attrVals.length) {
      for (var i = 0, len = attrNames.length; i < len; i++) {
        opt['query'].push(
          `attr-name=${encodeURIComponent(
            attrNames[i],
          )}&attr-val=${encodeURIComponent(attrVals[i])}`,
        )
        parsed.push(`${attrNames[i]}-${attrVals[i]}`)
      }
    }

    // Reassign (never mutate) so the filter panel re-renders its checkboxes
    filters = parsed

    // The URL is authoritative: clear search/sort when the params are absent
    search = urlParams.get('query') || ''
    opt['search'] = search

    sortBy = urlParams.get('sort-by') || ''
    opt['sortBy'] = sortBy

    return opt
  }

  function changeProductType(id, opt = {}) {
    if (id !== undefined) {
      products.setFilters({
        type: id,
        active: 1,
        ...opt,
      })
    }
  }

  afterUpdate(() => {
    if (meta.title) document.title = meta.title
    try {
      if (params.sale === true && !sale) {
        // User requests sale page, and is not currently on the sale page
        let opt = parseFilters()
        opt.sale = true
        changeProductType(2, opt)
        productTypeId = 0 // reset
        sale = true
        currentSearch = window.location.search
      } else if (!params.sale && params.tag && params.id !== productTypeId) {
        // User requests a tag for a different product type
        // NOTE: May not be relevant?
        let opt = parseFilters()
        opt.search = params.tag
        productTypeId = params.id
        changeProductType(params.id, opt)
        sale = false
        currentSearch = window.location.search
      } else if (
        !params.sale &&
        params.id !== productTypeId &&
        parseInt(params.id)
      ) {
        // User is just switching between product types; parseFilters resets
        // filters/sortBy/search from the new URL
        let opt = parseFilters()
        productTypeId = params.id
        changeProductType(params.id, opt)
        sale = false
        currentSearch = window.location.search
      } else if (window.location.search !== currentSearch) {
        // Same page, but the query string changed: either "Apply Filters"
        // navigated through the router, or the user pressed back/forward.
        // Re-parse the URL so the panel and product list stay in sync.
        let opt = parseFilters()
        // On a tag page the tag acts as the search term unless the user
        // typed their own keyword
        if (params.tag && !opt.search) {
          opt.search = params.tag
        }
        if (params.sale) {
          opt.sale = true
        }
        changeProductType(params.id, opt)
        currentSearch = window.location.search
      }
    } catch (ex) {
      console.log(ex)
    }
  })
</script>

<style type="text/less">
  .products-page {
    display: flex;
    max-width: var(--content-width);
    margin: auto;
    overflow: hidden;
    position: relative;
    align-items: stretch;
    padding: var(--spacing-sm);
  }

  .products {
    padding: 0 var(--spacing);
  }

  .title {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .products-sort {
    display: flex;
    align-items: center;
  }

  .products-list {
    list-style: none;
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    grid-gap: var(--spacing-sm);
    max-width: 100%;
    padding: 0;

    li {
      min-width: 16rem;
    }
  }

  .footer-links {
    display: flex;
    justify-content: space-between;
    flex-direction: column;
  }

  .footer-links * {
    margin-top: 1rem;
  }

  @media (min-width: 600px) {
    .products-list {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  @media (min-width: 800px) {
    .filter-btn {
      display: none;
    }

    .footer-links {
      display: flex;
      justify-content: space-between;
      flex-direction: row;
    }
  }
  @media (min-width: 1100px) {
    .products-list {
      grid-template-columns: repeat(3, 1fr);
    }

    .footer-links {
      display: flex;
      justify-content: space-between;
      flex-direction: row;
    }
  }
  @media (min-width: 1350px) {
    .products-list {
      grid-template-columns: repeat(4, 1fr);
    }

    .footer-links {
      display: flex;
      justify-content: space-between;
      flex-direction: row;
    }
  }

  @media (min-width: 800px) {
    .products-page {
      padding: var(--spacing);
    }
  }
</style>

<svelte:head>
  {#if meta.title}
    <title>{meta.title}</title>
    <meta name="description" content={meta.description} />
    <meta property="og:title" content={meta.title} />
    <meta property="og:description" content={meta.description} />
    <meta property="og:type" content="website" />
    <meta property="og:url" content={meta.canonical} />
    <link rel="canonical" href={meta.canonical} />
  {/if}
</svelte:head>

{#if infoMsg}
  <Popup message={infoMsg} />
{/if}

<div class="products-page">
  <ProductFilters
    bind:checkedBoxes={filters}
    bind:open={filtersOpen}
    sortBy={sortBy ? sortBy : defaultSort}
    productType={params.id}
    keyword={params.tag ? params.tag : search}
    active={1} />

  <div class="products">
    <div class="title">
      <div>
        <h2>{meta.heading || (params.id == 1 ? 'Rent Jigsaw Puzzles' : 'Purchase')}</h2>

        {#if params.id == 6}
          <a href="/redeem">Redeem a gift card</a>
          ·
          <a href="/gift-card/balance">Check your gift card balance</a>
        {/if}

        <button
          class="btn btn-link filter-btn"
          aria-label="Toggle filter side panel"
          on:click={() => (filtersOpen = !filtersOpen)}>
          Show Filters
          <svg class="icon">
            <use href="/icons.svg#keyboard_arrow_right" />
          </svg>
        </button>
      </div>
      <RentalQuota />
      <RentalQuotaSubscription
        subscription={$subscription}
        clearError={subscription.clearError} />
    </div>

    {#if $products.loading}
      <div class="text-center">
        <svg class="icon spin">
          <use href="/icons.svg#circle_notch" />
        </svg>
        Loading Puzzles...
      </div>
    {:else}
      <ul class="products-list">
        {#each $products.products as product, i}
          <li
            on:click={(e) => {
              products.setProductPageY(e.pageY)
            }}>
            <ProductPreview
              {product}
              subDetails={$subscription}
              userRentedPrev={$userRented[product.sku]} />
          </li>
        {/each}

        {#if $products.products.length === 0 && !$products.loading}
          <p>No products found!</p>
        {/if}
      </ul>

      <div id="products-bottom" class="footer-links">
        <a href="#" on:click={() => scrollToY(0)}>Scroll To Top</a>
        <button
          class="btn"
          disabled={$products.products.length === $products.total}
          on:click|preventDefault={(e) => {
            products.loadMore($products.products)
            scrollToY(e.pageY - 900)
          }}>
          Load More
        </button>
        <span>Viewing {$products.products.length} of {$products.total}</span>
      </div>
    {/if}
  </div>
</div>
